import { apiGet, apiPost, unwrap } from './apiClient';
import type {
  ResponseDTO,
  DeviceDetailDTO,
  DeviceSummaryDTO,
  DevicePolicySummaryDTO,
  MdmCertificateInventory,
  DeviceCheckInRequest,
  BulkDeviceLockRequest,
  BulkDeviceUnenrollRequest,
} from '@/types/api';

const BASE = '/api/v1/devices';

export async function listDevices(page = 0, size = 20) {
  const res = await apiGet<ResponseDTO<DeviceSummaryDTO[]>>(
    BASE,
    { params: { page, size }, includeTenantHeader: true }
  );
  return res;
}

export async function getDeviceDetail(uuid: string) {
  const res = await apiGet<ResponseDTO<DeviceDetailDTO>>(
    `${BASE}/data/${uuid}`,
    { includeTenantHeader: true }
  );
  return unwrap(res);
}

// Flat device summary (includes user fields like username/fullName/userEmail/userId
// that aren't always present on the wrapper returned by /data/{uuid}).
export async function getDeviceSummary(uuid: string) {
  const res = await apiGet<ResponseDTO<DeviceDetailDTO>>(
    `${BASE}/uuid/${uuid}`,
    { includeTenantHeader: true }
  );
  return unwrap(res);
}

// NOTE: The following helpers were removed as orphan endpoints with zero
// callers in the app — their data is already provided by getDeviceDetail()
// (which returns nested applications + certificates) or by listDevices():
//   - getDeviceSummary()    → GET /api/v1/devices/uuid/{uuid}
//   - getDeviceBySerial()   → GET /api/v1/devices/serial/{serial}
//   - getDeviceApps()       → GET /api/v1/devices/{id}/applications
//   - getDeviceManagedApps()→ GET /api/v1/devices/{id}/managed-apps
// If a future feature needs them, re-add against the current backend contract.

export async function getDeviceCerts(deviceId: string) {
  const res = await apiGet<ResponseDTO<MdmCertificateInventory[]>>(
    `${BASE}/${deviceId}/certificates`,
    { includeTenantHeader: true }
  );
  return unwrap(res);
}

export async function getDevicePolicies(deviceId: string, page = 0, size = 20) {
  const res = await apiGet<ResponseDTO<DevicePolicySummaryDTO[]>>(
    `${BASE}/${deviceId}/policies`,
    { params: { page, size }, includeTenantHeader: true }
  );
  return unwrap(res);
}

export async function checkInDevice(body: DeviceCheckInRequest) {
  const res = await apiPost<ResponseDTO<string>>(
    `${BASE}/check-in`,
    body,
    { includeTenantHeader: true }
  );
  return unwrap(res);
}

// NOTE: The legacy `inviteUser()` helper that POSTed to
// /api/v1/devices/invite-user has been removed. The Enroll Device flow
// now sends invitations via `sendTemplatedEmail()` in services/emailApi.ts
// (POST /api/v1/emails/send with template_type "INVITE_USER") so the
// message respects the tenant's configured SMTP settings and template.

export async function bulkLock(body: BulkDeviceLockRequest) {
  const res = await apiPost<ResponseDTO<Record<string, string>>>(
    `${BASE}/bulk-lock`,
    body,
    { includeTenantHeader: true }
  );
  return unwrap(res);
}

export async function bulkUnlock(body: { deviceIds: string[] }) {
  const res = await apiPost<ResponseDTO<Record<string, string>>>(
    `${BASE}/bulk-unlock`,
    body,
    { includeTenantHeader: true }
  );
  return unwrap(res);
}

export async function bulkUnenroll(body: BulkDeviceUnenrollRequest) {
  const res = await apiPost<ResponseDTO<Record<string, string>>>(
    `${BASE}/bulk-unenroll`,
    body,
    { includeTenantHeader: true }
  );
  return unwrap(res);
}
